import { useEffect, useRef } from "react";
import * as THREE from "three";
import { disposeScene, useActiveWhenVisible, useSceneMode, usePointerParallax } from "./scene-utils";
function HeartScene({
  intensity = 2,
  onReady,
  className,
  mode = "teaser",
  scrollRef
}) {
  const hostRef = useRef(null);
  const { mode: sceneMode, tier } = useSceneMode();
  const active = useActiveWhenVisible(hostRef);
  const parallax = usePointerParallax(sceneMode === "live");
  const activeRef = useRef(true);
  activeRef.current = active;
  const readyRef = useRef(false);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || sceneMode === "off") return;
    const width = () => host.clientWidth || 1;
    const height = () => host.clientHeight || 1;
    const renderer = new THREE.WebGLRenderer({
      antialias: tier.quality !== "low",
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, tier.dpr));
    renderer.setSize(width(), height(), false);
    renderer.setClearColor(0, 0);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = mode === "reveal" ? 1.18 : 1.05;
    host.appendChild(renderer.domElement);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(656647, 0.028);
    const camera = new THREE.PerspectiveCamera(38, width() / height(), 0.1, 200);
    camera.position.set(0, 0.15, 7.4);
    const group = new THREE.Group();
    scene.add(group);
    const shape = new THREE.Shape();
    const segs = tier.quality === "low" ? 80 : 160;
    for (let i = 0; i <= segs; i++) {
      const t = i / segs * Math.PI * 2;
      const x = 16 * Math.pow(Math.sin(t), 3);
      const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
      const px = x / 17;
      const py = y / 17;
      if (i === 0) shape.moveTo(px, py);
      else shape.lineTo(px, py);
    }
    shape.closePath();
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.7,
      bevelEnabled: true,
      bevelSegments: tier.quality === "low" ? 2 : 5,
      bevelSize: 0.07,
      bevelThickness: 0.075,
      curveSegments: tier.quality === "low" ? 8 : 20
    });
    geometry.center();
    geometry.computeVertexNormals();
    const heartMat = tier.quality === "low" ? new THREE.MeshStandardMaterial({
      color: new THREE.Color("#4c2a8c"),
      roughness: 0.25,
      metalness: 0.2,
      emissive: new THREE.Color("#1e0f3d"),
      emissiveIntensity: mode === "reveal" ? 0.9 : 0.5,
      transparent: true,
      opacity: 0.97
    }) : new THREE.MeshPhysicalMaterial({
      color: new THREE.Color("#4c2a8c"),
      roughness: 0.22,
      metalness: 0.18,
      clearcoat: 1,
      clearcoatRoughness: 0.18,
      emissive: new THREE.Color("#1e0f3d"),
      emissiveIntensity: mode === "reveal" ? 0.9 : 0.5,
      sheen: 0.85,
      sheenColor: new THREE.Color("#f0a9ba"),
      sheenRoughness: 0.42,
      // Semi-translucent so the core layer glows through: this is the "soft
      // inner glow" — light from inside the object, not a blur drawn on top.
      transparent: true,
      opacity: 0.97
    });
    const heart = new THREE.Mesh(geometry, heartMat);
    heart.scale.setScalar(1.02);
    group.add(heart);
    const coreMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color("#ff5f7a"),
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const core = new THREE.Mesh(geometry, coreMat);
    core.scale.setScalar(0.9);
    core.position.z = -0.06;
    group.add(core);
    if (tier.quality !== "low") {
      const haloGeo = new THREE.EdgesGeometry(geometry, 30);
      const haloMat = new THREE.LineBasicMaterial({
        color: new THREE.Color("#e9d5ff"),
        transparent: true,
        opacity: 0.3,
        blending: THREE.AdditiveBlending
      });
      const halo = new THREE.LineSegments(haloGeo, haloMat);
      halo.scale.setScalar(1.045);
      group.add(halo);
    }
    scene.add(new THREE.AmbientLight(5974581, 1.15));
    const key = new THREE.PointLight(16767392, mode === "reveal" ? 95 : 58, 26, 2);
    key.position.set(3.4, 3.6, 4.6);
    scene.add(key);
    const rim = new THREE.PointLight(15772090, 46, 22, 2);
    rim.position.set(-3.8, -1.8, 2.4);
    scene.add(rim);
    const under = new THREE.PointLight(9049380, 30, 20, 2);
    under.position.set(0, -3.6, -1.6);
    scene.add(under);
    const back = new THREE.PointLight(13228287, 16, 24, 2);
    back.position.set(-1.2, 2.8, -5);
    scene.add(back);
    const pCount = Math.round(tier.particleCount * (mode === "reveal" ? 1.25 : 1));
    const pPos = new Float32Array(pCount * 3);
    const pSize = new Float32Array(pCount);
    const pPhase = new Float32Array(pCount);
    const FOCAL_Z = 2.2;
    for (let i = 0; i < pCount; i++) {
      const r = 3 + Math.random() * 5.8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.cos(phi) * 0.75;
      const z = r * Math.sin(phi) * Math.sin(theta) * 0.85;
      pPos[i * 3] = x;
      pPos[i * 3 + 1] = y;
      pPos[i * 3 + 2] = z;
      const defocus = Math.min(1, Math.abs(z - FOCAL_Z) / 7);
      pSize[i] = (0.045 + defocus * 0.115) * (0.75 + Math.random() * 0.5);
      pPhase[i] = Math.random() * Math.PI * 2;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
    pGeo.setAttribute("aSize", new THREE.BufferAttribute(pSize, 1));
    pGeo.setAttribute("aPhase", new THREE.BufferAttribute(pPhase, 1));
    const dotCanvas = document.createElement("canvas");
    dotCanvas.width = dotCanvas.height = 64;
    const dctx = dotCanvas.getContext("2d");
    if (dctx) {
      const g = dctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, "rgba(245,240,255,1)");
      g.addColorStop(0.3, "rgba(200,190,255,0.9)");
      g.addColorStop(0.65, "rgba(233,168,242,0.32)");
      g.addColorStop(1, "rgba(233,168,242,0)");
      dctx.fillStyle = g;
      dctx.fillRect(0, 0, 64, 64);
    }
    const dotTex = new THREE.CanvasTexture(dotCanvas);
    dotTex.needsUpdate = true;
    const pMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMap: { value: dotTex },
        uOpacity: { value: mode === "reveal" ? 0.95 : 0.8 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, tier.dpr) }
      },
      vertexShader: (
        /* glsl */
        `
        attribute float aSize;
        attribute float aPhase;
        uniform float uTime;
        uniform float uPixelRatio;
        varying float vAlpha;
        void main() {
          vec3 p = position;
          // Slow upward drift + a lazy lateral sway, so the field breathes.
          p.y = mod(p.y + uTime * 0.09 + 8.0, 16.0) - 8.0;
          p.x += sin(uTime * 0.22 + aPhase) * 0.22;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = aSize * uPixelRatio * (240.0 / -mv.z);
          // Twinkle: each mote has its own phase, so the field shimmers rather
          // than pulsing as one block.
          vAlpha = 0.55 + 0.45 * sin(uTime * 0.9 + aPhase * 3.0);
        }
      `
      ),
      fragmentShader: (
        /* glsl */
        `
        uniform sampler2D uMap;
        uniform float uOpacity;
        varying float vAlpha;
        void main() {
          vec4 tex = texture2D(uMap, gl_PointCoord);
          gl_FragColor = vec4(tex.rgb, tex.a * vAlpha * uOpacity);
          if (gl_FragColor.a < 0.01) discard;
        }
      `
      ),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    const points = new THREE.Points(pGeo, pMat);
    scene.add(points);
    const petalShape = new THREE.Shape();
    petalShape.moveTo(0, -0.5);
    petalShape.bezierCurveTo(0.42, -0.28, 0.34, 0.3, 0, 0.5);
    petalShape.bezierCurveTo(-0.34, 0.3, -0.42, -0.28, 0, -0.5);
    const petalGeo = new THREE.ExtrudeGeometry(petalShape, {
      depth: 0.03,
      bevelEnabled: true,
      bevelSize: 0.012,
      bevelThickness: 0.012,
      bevelSegments: 1,
      curveSegments: tier.quality === "low" ? 5 : 9
    });
    petalGeo.center();
    const petalMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color("#d38be6"),
      roughness: 0.55,
      metalness: 0,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.86
    });
    const petalCount = tier.petalCount;
    const petals = new THREE.InstancedMesh(petalGeo, petalMat, petalCount);
    const petalColors = ["#f4a6c6", "#c15fd6", "#5b3fa8", "#e9a8f2", "#f0b8c6"];
    for (let i = 0; i < petalCount; i++) {
      petals.setColorAt(i, new THREE.Color(petalColors[i % petalColors.length]));
    }
    if (petals.instanceColor) petals.instanceColor.needsUpdate = true;
    const petalState = Array.from({ length: petalCount }, (_, i) => {
      const band = i % 3;
      const z = band === 0 ? -3.2 - Math.random() * 2 : band === 1 ? (Math.random() - 0.5) * 3 : 2.6 + Math.random() * 2.6;
      return {
        x: (Math.random() - 0.5) * 12,
        y: Math.random() * 14 - 7,
        z,
        speed: 0.16 + Math.random() * 0.34,
        spin: (Math.random() - 0.5) * 1.1,
        tumble: (Math.random() - 0.5) * 1.6,
        phase: Math.random() * Math.PI * 2,
        sway: 0.35 + Math.random() * 0.85,
        scale: (band === 2 ? 1.25 : 0.72) + Math.random() * 0.6
      };
    });
    scene.add(petals);
    const dummy = new THREE.Object3D();
    const onResize = () => {
      const w = width();
      const h = height();
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      const ar = w / h;
      camera.fov = ar < 0.7 ? 58 : ar < 0.85 ? 50 : 38;
      camera.updateProjectionMatrix();
    };
    onResize();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    ro?.observe(host);
    const clock = new THREE.Clock();
    const amp = 0.4 + intensity * 0.3;
    let frame = 0;
    const renderFrame = (t) => {
      const breathe = 1 + Math.sin(t * 0.85) * 0.02 * amp;
      group.scale.setScalar(breathe);
      group.rotation.y = Math.sin(t * 0.3) * 0.16 * amp;
      group.rotation.x = Math.sin(t * 0.23) * 0.06 * amp;
      const beat = (() => {
        const c = t * 0.62 % 1;
        const first = Math.exp(-Math.pow((c - 0.06) / 0.05, 2));
        const second = Math.exp(-Math.pow((c - 0.24) / 0.055, 2)) * 0.62;
        return first + second;
      })();
      heartMat.emissiveIntensity = (mode === "reveal" ? 0.9 : 0.5) + beat * 0.55 * amp;
      coreMat.opacity = 0.24 + beat * 0.3 * amp;
      group.scale.multiplyScalar(1 + beat * 8e-3);
      const scroll = scrollRef?.current ?? 0;
      const px = parallax.current.x;
      const py = parallax.current.y;
      camera.position.x = Math.sin(t * 0.17) * 0.45 * amp + px * 0.85;
      camera.position.y = 0.15 + Math.sin(t * 0.21) * 0.19 * amp + py * 0.55 + scroll * 0.9;
      camera.position.z = 7.5 - Math.sin(t * 0.11) * 0.22 * amp - scroll * 1.5;
      camera.lookAt(px * 0.18, py * 0.14 + scroll * 0.35, 0);
      pMat.uniforms.uTime.value = t;
      points.rotation.y = t * 0.03;
      for (let i = 0; i < petalCount; i++) {
        const p = petalState[i];
        p.y -= p.speed * 0.011;
        if (p.y < -7.5) {
          p.y = 7.5;
          p.x = (Math.random() - 0.5) * 12;
        }
        const swayX = Math.sin(t * 0.42 + p.phase) * p.sway;
        const swayZ = Math.cos(t * 0.31 + p.phase) * 0.3;
        dummy.position.set(p.x + swayX, p.y, p.z + swayZ);
        dummy.rotation.set(
          t * p.tumble * 0.5 + p.phase,
          t * p.spin * 0.7 + p.phase * 0.5,
          Math.sin(t * 0.55 + p.phase) * 0.9
        );
        dummy.scale.setScalar(p.scale);
        dummy.updateMatrix();
        petals.setMatrixAt(i, dummy.matrix);
      }
      petals.instanceMatrix.needsUpdate = true;
      key.intensity = (mode === "reveal" ? 95 : 58) * (1 + Math.sin(t * 1.7) * 0.06);
      rim.intensity = 46 * (1 + beat * 0.25);
    };
    const start = () => {
      if (sceneMode === "static") {
        renderFrame(0);
        renderer.render(scene, camera);
        if (!readyRef.current) {
          readyRef.current = true;
          onReady?.();
        }
        return;
      }
      const tick = () => {
        if (activeRef.current) {
          const t = clock.getElapsedTime();
          renderFrame(t);
          renderer.render(scene, camera);
          if (!readyRef.current) {
            readyRef.current = true;
            onReady?.();
          }
        }
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    start();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro?.disconnect();
      disposeScene(scene);
      dotTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === host) host.removeChild(renderer.domElement);
    };
  }, [sceneMode, tier, intensity, mode, onReady, scrollRef]);
  if (sceneMode === "off") return <CSSHeartFallback className={className} />;
  return <div
    ref={hostRef}
    className={className}
    aria-hidden="true"
    data-testid="heart-scene"
    style={{ width: "100%", height: "100%" }}
  />;
}
function CSSHeartFallback({ className }) {
  return <div
    className={className}
    style={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}
    data-testid="heart-fallback"
  >
      <div style={{ position: "relative", width: "min(52vw, 300px)", aspectRatio: "1" }}>
        <div
    aria-hidden="true"
    style={{
      position: "absolute",
      inset: "12%",
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(244,166,198,0.6), rgba(76,29,149,0.28) 55%, transparent 72%)",
      filter: "blur(30px)",
      animation: "lux-glow 6s ease-in-out infinite"
    }}
  />
        <svg viewBox="0 0 32 30" width="100%" height="100%" aria-hidden="true">
          <defs>
            <radialGradient id="heartFill" cx="34%" cy="26%" r="82%">
              <stop offset="0%" stopColor="#c15fd6" />
              <stop offset="46%" stopColor="#4c2a8c" />
              <stop offset="100%" stopColor="#1b1440" />
            </radialGradient>
            <linearGradient id="heartRim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#e9a8f2" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#f4a6c6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#e9a8f2" stopOpacity="0.8" />
            </linearGradient>
          </defs>
          <path
    d="M16 28.5C16 28.5 1.5 19.7 1.5 10.9 1.5 5.7 5.4 2 9.8 2c2.9 0 4.9 1.5 6.2 3.3C17.3 3.5 19.3 2 22.2 2c4.4 0 8.3 3.7 8.3 8.9 0 8.8-14.5 17.6-14.5 17.6z"
    fill="url(#heartFill)"
    stroke="url(#heartRim)"
    strokeWidth="0.35"
  />
        </svg>
      </div>
    </div>;
}
export {
  CSSHeartFallback,
  HeartScene as default
};
